import { useSyncExternalStore } from "react";

/**
 * Modo sandbox: o web passa a mandar `X-Sandbox: true` para a API do JapExpress,
 * que então fala com o sandbox do Core em vez da produção. O sandbox do Core grava
 * no mesmo banco da produção, então pedidos criados nele são reais.
 *
 * O estado fica numa chave própria, separada da sessão: sair da conta não desliga
 * o sandbox, e quem testa troca de usuário sem repetir o gesto.
 */
export const SANDBOX_STORAGE_KEY = "@japexpress/sandbox";
export const SANDBOX_HEADER = "X-Sandbox";

export const SANDBOX_TAPS_REQUIRED = 6;
export const SANDBOX_TAP_WINDOW_MS = 2000;

export type TapState = { count: number; lastAt: number };

export const initialTapState: TapState = { count: 0, lastAt: 0 };

/**
 * Contador por timestamp, sem timers: um intervalo maior que a janela entre dois
 * toques reinicia a contagem. Ao chegar no último toque dispara e zera.
 */
export function registerTap(
  state: TapState,
  now: number
): { state: TapState; triggered: boolean } {
  const continues =
    state.count > 0 && now - state.lastAt <= SANDBOX_TAP_WINDOW_MS;
  const count = continues ? state.count + 1 : 1;

  if (count >= SANDBOX_TAPS_REQUIRED) {
    return { state: { count: 0, lastAt: now }, triggered: true };
  }

  return { state: { count, lastAt: now }, triggered: false };
}

const readStored = (): boolean => {
  try {
    return localStorage.getItem(SANDBOX_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

// Fonte da verdade em memória: se o storage estiver bloqueado, o modo ainda
// funciona durante a sessão, só não sobrevive ao recarregar.
let enabled = readStored();

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

export const isSandboxEnabled = (): boolean => enabled;

/** Inverte o modo sandbox e devolve o novo valor. */
export function toggleSandbox(): boolean {
  enabled = !enabled;

  try {
    if (enabled) localStorage.setItem(SANDBOX_STORAGE_KEY, "true");
    else localStorage.removeItem(SANDBOX_STORAGE_KEY);
  } catch {
    // sem storage: vale só até recarregar
  }

  emit();
  return enabled;
}

if (typeof window !== "undefined") {
  // Mantém abas abertas em sincronia (key === null é localStorage.clear()).
  window.addEventListener("storage", (event) => {
    if (event.key !== SANDBOX_STORAGE_KEY && event.key !== null) return;
    enabled = readStored();
    emit();
  });
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useSandbox = (): boolean =>
  useSyncExternalStore(subscribe, isSandboxEnabled, () => false);
